import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { useSetCouponCodeFromParamsToCookie } from "@circle-react/hooks/useSetCouponCodeFromParamsToCookie";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import type { OnSubmitFunction } from "@circle-react-uikit/Form/Form";
import { useToast } from "@circle-react-uikit/ToastV2";

interface SignInFormState {
  chat_bot_session_id: string | null;
  source: string | null;
  user: {
    email: string;
    password: string;
    community_id?: number;
  };
}

interface SignInResponse {
  message?: string;
  redirect_url?: string;
}

interface SignInError {
  message: string;
}

export const SignInForm = () => {
  useSetCouponCodeFromParamsToCookie();
  const toast = useToast();

  const localeScope = "authentication_app.signin.form";
  const buttonText = t(`${localeScope}.submit`);
  const defaultFormState = {
    user: {
      email: "",
      password: "",
    },
  };

  const { mutateAsync: handleSubmit, isLoading } = useMutation<
    SignInResponse,
    SignInError,
    SignInFormState
  >(
    data =>
      reactQueryPost<SignInResponse>(
        authenticationAppRoutes.discoverSignIn(),
        data,
      ),
    {
      onSuccess: data => {
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        }
      },
      onError: data => toast.error(data.message),
    },
  );

  const onSubmit: OnSubmitFunction<SignInFormState> = async values => {
    await handleSubmit(values);
  };

  return (
    <Form<SignInFormState>
      action={authenticationAppRoutes.discoverSignIn({ params: {} })}
      method="POST"
      defaultValues={defaultFormState}
      onSubmit={onSubmit}
    >
      <Form.Item
        isFirstChild
        id="user_email"
        name="user[email]"
        label={t(`${localeScope}.email`)}
        hideDescription
        hideBorder
        rules={{
          required: t(`${localeScope}.errors.email_required`),
        }}
      >
        <Form.Input autoFocus />
      </Form.Item>

      <Form.Item
        id="user_password"
        name="user[password]"
        label={t(`${localeScope}.password`)}
        hideDescription
        hideBorder
        rules={{
          required: t(`${localeScope}.errors.password_required`),
        }}
      >
        <Form.Input type="password" />
      </Form.Item>

      <div className="mt-6">
        <Button
          type="submit"
          name="commit"
          variant="primary"
          disabled={isLoading}
          extraLarge
          full
        >
          {buttonText}
        </Button>
      </div>
    </Form>
  );
};
